import { useLayoutEffect, useRef } from "react";
import { angleColour, quadrantAt, QUADRANT_COLOURS, QUADRANTS, type PresentationStyle } from "./presentationProfiles";
import type { PresentationSample } from "./positionSeries";

const NEUTRAL:Record<string,string>={upright:"#b8c0c8",inverted:"#929ca8",Invalid:"#d4c1c1",Missing:"#e5e8ed","Mixed / unavailable":"#dedee7"};
export function presentationValue(sample:PresentationSample,angle:boolean,categories:readonly string[]):string {
  return sample.value===null?sample.state:angle?`${sample.value.toFixed(1)}°`:(categories[sample.value]||"Invalid");
}
/** A shared band/step painter for display categories and cyclic angles. */
export function PresentationPlot(props:{samples:PresentationSample[];interval:{start_seconds:number;end_seconds:number};style:PresentationStyle;angle?:boolean;compact?:boolean;showLegend?:boolean;reverseCategories?:boolean;categories:readonly string[];colours:readonly string[];onSeek(seconds:number):void;label:string}) {
  const ref=useRef<HTMLCanvasElement>(null);
  const height=props.compact?36:props.angle?76:92;
  useLayoutEffect(()=>{
    const canvas=ref.current;if(!canvas)return;
    const draw=()=>{
      const width=Math.max(1,canvas.getBoundingClientRect().width),ratio=Math.min(window.devicePixelRatio||1,2);
      canvas.width=Math.round(width*ratio);canvas.height=height*ratio;
      const ctx=canvas.getContext("2d");if(!ctx)return;
      ctx.setTransform(ratio,0,0,ratio,0,0);ctx.clearRect(0,0,width,height);
      const x=(t:number)=>(t-props.interval.start_seconds)/(props.interval.end_seconds-props.interval.start_seconds)*width;
      const inset=props.compact?3:props.angle?8:12;
      const y=(v:number)=>props.angle?height-inset-v/360*(height-2*inset):inset+(props.reverseCategories?1-v/Math.max(1,props.categories.length-1):v/Math.max(1,props.categories.length-1))*(height-2*inset);
      if(props.style === "trace") {
        ctx.font=props.compact?"9px sans-serif":"10px sans-serif";ctx.fillStyle="#556577";ctx.strokeStyle="#dce2e8";
        (props.angle?(props.compact?[0,180,360]:[0,90,180,270,360]):props.categories.map((_,i)=>i)).forEach(value=>{
          ctx.beginPath();ctx.moveTo(0,y(value));ctx.lineTo(width,y(value));ctx.stroke();
          ctx.fillText(props.angle?`${value}°`:props.categories[value],3,props.compact?Math.max(9,Math.min(height-2,y(value)+3)):y(value)-2);
        });
      }
      let previous:PresentationSample|undefined;
      const runs:PresentationSample[]=[];
      for(const sample of props.samples) {
        const last=runs[runs.length-1];
        if(last && last.value===sample.value && last.state===sample.state && Math.abs(last.end-sample.start)<1e-7 && (props.style === "bands" || sample.value===null))last.end=sample.end;
        else runs.push({...sample});
      }
      for(const sample of runs) {
        if(sample.end<=props.interval.start_seconds||sample.start>=props.interval.end_seconds)continue;
        const left=x(sample.start),right=x(sample.end),value=sample.value;
        if(value===null) {
          ctx.fillStyle=NEUTRAL[sample.state]||NEUTRAL.Missing;ctx.fillRect(left,0,Math.max(1,right-left),height);if(right-left>55){ctx.fillStyle="#334155";ctx.font="11px sans-serif";ctx.fillText(sample.state,left+3,height/2);}previous=undefined;continue;
        }
        const colour=props.angle?angleColour(value):props.colours[value];
        if(props.style === "bands") {
          ctx.fillStyle=colour;ctx.fillRect(left,0,Math.max(.5,right-left),height);
          if(right-left>64){ctx.fillStyle="#152438";ctx.font="11px sans-serif";ctx.fillText(presentationValue(sample,!!props.angle,props.categories),left+3,height/2);}
        } else {
          ctx.strokeStyle=colour;ctx.lineWidth=2;ctx.beginPath();
          // A discontinuity, neutral interval or >180° wrap always starts a new path.
          if(previous?.value!=null&&Math.abs(previous.end-sample.start)<1e-7&&(!props.angle||Math.abs(previous.value-value)<=180)) {
            ctx.moveTo(x(previous.start),y(previous.value));
            if(!props.angle)ctx.lineTo(left,y(previous.value));
            ctx.lineTo(left,y(value));
          } else ctx.moveTo(left,y(value));
          if(!props.angle)ctx.lineTo(right,y(value));else {ctx.moveTo(left,y(value));ctx.lineTo(left+.8,y(value));}
          ctx.stroke();
        }
        previous=sample;
      }
    };
    draw();const observer=typeof ResizeObserver === "undefined"?null:new ResizeObserver(draw);observer?.observe(canvas);return ()=>observer?.disconnect();
  },[props.samples,props.interval,props.style,props.angle,props.categories,props.colours,props.compact,props.reverseCategories,height]);
  return <div className="presentation-lane" data-representation={props.angle?"angle":"categories"} data-style={props.style}>
    <span className="presentation-lane-label">{props.compact&&!props.angle?"Quadrants":props.label}</span>
    <canvas ref={ref} role="img" aria-label={`${props.label}; ${props.style}; click to seek`} style={{height}} onClick={event=>{const rect=event.currentTarget.getBoundingClientRect();props.onSeek(props.interval.start_seconds+Math.max(0,Math.min(1,(event.clientX-rect.left)/rect.width))*(props.interval.end_seconds-props.interval.start_seconds));}}/>
    {props.showLegend!==false && <div className="presentation-legend">{props.categories.map((label,index)=><span key={label}><i style={{background:props.colours[index]}}/>{props.angle?`${index*90}° `:""}{label}</span>)}<span>{props.angle || props.categories===QUADRANTS?"Neutral: upright / inverted / missing / invalid":"Neutral: missing / invalid / mixed"}</span></div>}
  </div>;
}
export function quadrantSamples(samples:PresentationSample[]):PresentationSample[] {
  return samples.map(sample=>({...sample,value:sample.value===null?null:quadrantAt(sample.value)}));
}
export const POSITION_LEGEND={categories:QUADRANTS,colours:QUADRANT_COLOURS};
